import { t } from "i18next"
import { Number as NumberDisplay } from "components/common"

export const PAYMENTS_FORM_LIST_NAME = "payments"

export function mergePaymentsRowPatch(form, listIndex, patch) {
  const payments = [...(form.getFieldValue(PAYMENTS_FORM_LIST_NAME) || [])]
  payments[listIndex] = { ...(payments[listIndex] || {}), ...patch }
  form.setFieldsValue({ [PAYMENTS_FORM_LIST_NAME]: payments })
}

export const getTotalRequiredPaymentAmount = ({ vasEnabled, leadSalesInfo, vasFees, minTokenDetails, dealPrice }) => {
  if (vasEnabled && leadSalesInfo?.isFirstSalesTask) return Math.round(vasFees || 0)
  if (vasEnabled && leadSalesInfo?.isVasRemaining) return Math.round(leadSalesInfo?.vasRemianingAmount || 0)
  return Math.round(
    leadSalesInfo?.token !== undefined
      ? leadSalesInfo?.tokenUnit === "percentage"
        ? (leadSalesInfo?.token / 100) * dealPrice
        : leadSalesInfo?.token
      : minTokenDetails.minTokenUnit === "percentage"
        ? minTokenDetails.tokenWithUnit * dealPrice
        : minTokenDetails.minToken
  )
}

export const paymentsListRequiresMinimumTotal = ({ vasEnabled, leadSalesInfo, lastPaymentStatus, lastPaymentType }) => {
  const firstSales = leadSalesInfo?.isFirstSalesTask
  const vasRemaining = leadSalesInfo?.isVasRemaining
  const rejectedToken = lastPaymentStatus === "rejected" && lastPaymentType === "token"
  return (vasEnabled && firstSales) || (vasEnabled && vasRemaining) || !leadSalesInfo || firstSales || rejectedToken
}

export const getProjectCdpAmount = ({ project, cdpCommitment, dealPrice }) => {
  const pct = cdpCommitment ?? project?.ProjectFinancial?.completeDownPayment ?? 0
  return Math.round((dealPrice || 0) * pct * 0.01)
}

export const validatePaymentsListTotals = ({
  listValue,
  vasEnabled,
  leadSalesInfo,
  vasFees,
  minTokenDetails,
  dealPrice,
  project,
  cdpCommitment,
  lastPaymentStatus,
  lastPaymentType,
}) => {
  const sum = (listValue || []).reduce((acc, row) => acc + (+row?.amount || 0), 0)

  if (
    paymentsListRequiresMinimumTotal({
      vasEnabled,
      leadSalesInfo,
      lastPaymentStatus,
      lastPaymentType,
    })
  ) {
    const required = getTotalRequiredPaymentAmount({
      vasEnabled,
      leadSalesInfo,
      vasFees,
      minTokenDetails,
      dealPrice,
    })
    if (sum < required) {
      return Promise.reject(t("Total payment amount must be greater than or equal to required amount"))
    }
    return Promise.resolve()
  }

  const remain = leadSalesInfo?.remainComDowPayment
  if (remain != null && remain > 0) {
    if (sum > Math.round(remain)) {
      return Promise.reject(t("Total payment cannot exceed the remaining CDP amount"))
    }
    return Promise.resolve()
  }

  const projectCdp = getProjectCdpAmount({ project, cdpCommitment, dealPrice })
  if (projectCdp > 0 && sum >= projectCdp) {
    return Promise.reject(t("Total payment must be less than the complete down payment (CDP) amount"))
  }

  return Promise.resolve()
}

export const getPaymentSuffixMessage = ({ amount, dealPrice, vasEnabled, leadSalesInfo, vasFees }) => {
  const amountEntered = +amount
  if (amountEntered !== null && dealPrice === 0) return ""

  const remainingCdp = leadSalesInfo?.remainComDowPayment
  const remainingPdp = leadSalesInfo?.remainParDowPayment

  if (vasEnabled && leadSalesInfo?.isFirstSalesTask) {
    return amountEntered === Math.round(vasFees) ? t("VAS Fee Done") : ""
  }

  if (vasEnabled && leadSalesInfo?.isVasRemaining) {
    return amountEntered === Math.round(leadSalesInfo?.vasRemianingAmount) ? t("VAS Remaining Fee Done") : ""
  }

  if (leadSalesInfo?.current >= 0) {
    if (amountEntered >= remainingCdp) return t("Complete Payment Done")
    if (amountEntered >= remainingPdp && amountEntered < remainingCdp) return t("Partial Payment Done")
    return ""
  }

  return ""
}

export const formatAmountSuffix = (message, amount, numberProps) => (
  <>
    {message ? `${message}:` : ""} <NumberDisplay {...numberProps} value={amount} />
  </>
)

export const getNextTaskKey = ({
  value,
  leadSalesInfo,
  dealPrice,
  cdpCommitment,
  project,
  saleEvent,
  tokenDetails,
  vasEnabled,
  vasFees,
}) => {
  const remainingCdp = leadSalesInfo?.remainComDowPayment
  const remainingPdp = leadSalesInfo?.remainParDowPayment
  const projectCdp = Math.round(dealPrice * (cdpCommitment ?? project?.ProjectFinancial?.completeDownPayment) * 0.01)
  const projectPdp = Math.round(dealPrice * project?.pdpPercentage)
  const tokenScheduleAmount =
    tokenDetails?.tokenWithUnit > 1 ? tokenDetails?.minToken : tokenDetails?.tokenWithUnit * dealPrice
  const minBaseAmount = Math.round(
    saleEvent?.minAmount != null ? saleEvent.minAmount : vasEnabled ? vasFees : tokenScheduleAmount
  )

  if (leadSalesInfo?.current >= 0) {
    if (value >= remainingCdp) return "do_nothing"
    if (value >= remainingPdp && value < remainingCdp) return "complete_down_payment"
    if (value < remainingPdp && value >= leadSalesInfo?.remainMinToken) return "partial_down_payment"
  }

  if (value >= projectCdp) return "do_nothing"
  if (value >= projectPdp && value < projectCdp) return "complete_down_payment"
  if (value < projectPdp && value >= minBaseAmount) return "partial_down_payment"

  return null
}

export const createFormItemConfig = ({
  modeOfPayment,
  paymentProof,
  hasPaymentProof,
  setFieldsValue,
  getFieldValue,
  optionList,
  debouncedReferenceValidator,
  referenceError,
  referenceValidity,
  userPlatformKey,
  formItemConstants,
  payorderBank,
  chequeBank,
}) => {
  const shouldRender = type =>
    !!((hasPaymentProof && type?.imageData) || (!hasPaymentProof && typeof type === "object"))

  const {
    cardPaymentFormProps,
    cardPaymentFormChildren,
    payorderFormProps,
    payorderFormChildren,
    depositSlipFormProps,
    depositSlipFormChildren,
    onlinePaymentFormProps,
    onlinePaymentFormChildren,
    chequeFormProps,
    chequeFormChildren,
  } = formItemConstants

  return {
    card_payment: {
      conditionToCheckRender: shouldRender(paymentProof),
      extraCondition: modeOfPayment === "card_payment",
      formItemPropList: cardPaymentFormProps(false),
      formItemChildrenList: cardPaymentFormChildren({ setFieldsValue }),
    },
    bank_order: {
      extraCondition: true,
      conditionToCheckRender: true,
      formItemPropList: payorderFormProps({ referenceError, referenceValidity, isRequired: false }),
      formItemChildrenList: payorderFormChildren({
        setFieldsValue,
        optionList,
        getFieldValue,
        debouncedReferenceValidator,
        modeofPayment: modeOfPayment,
        payorderBank,
      }),
    },
    bank_deposit_slip: {
      extraCondition: true,
      conditionToCheckRender: shouldRender(paymentProof),
      formItemPropList: depositSlipFormProps(false),
      formItemChildrenList: depositSlipFormChildren({ setFieldsValue, optionList }),
    },
    online_payment: {
      extraCondition: true,
      conditionToCheckRender: shouldRender(paymentProof),
      formItemPropList: onlinePaymentFormProps(userPlatformKey, false),
      formItemChildrenList: onlinePaymentFormChildren({ setFieldsValue, optionList, isRequired: false }),
    },
    cheque: {
      extraCondition: true,
      conditionToCheckRender: shouldRender(paymentProof),
      formItemPropList: chequeFormProps({ referenceError, referenceValidity, isRequired: false }),
      formItemChildrenList: chequeFormChildren({
        setFieldsValue,
        optionList,
        getFieldValue,
        debouncedReferenceValidator,
        modeofPayment: modeOfPayment,
        chequeBank,
        isRequired: false,
      }),
    },
  }
}

export const hasPaymentProofValue = proof =>
  !!(proof && (proof?.fileList?.length || proof?.url || (Array.isArray(proof) && proof.length > 0)))

export const isPaymentProofUploading = proof => {
  const list = proof?.fileList
  if (!Array.isArray(list) || !list.length) return false
  return list.some(f => f?.status === "uploading")
}

export const paymentProofMatches = (a, b) => {
  if (a === b) return true
  if (!a && !b) return true
  if (!a || !b) return false
  return (a?.url ?? "") === (b?.url ?? "") && (a?.name ?? "") === (b?.name ?? "")
}

export const roundMoneyToTwoDecimals = n => {
  const x = +n
  if (!x) return 0
  return Math.round(x * 100) / 100
}

export const parsePaymentLineAmount = raw => {
  if (raw == null || raw === "") return NaN
  let s = String(raw).trim()
  if (!s) return NaN
  s = s.replace(/,/g, "").replace(/\s/g, "")
  s = s.replace(/^[A-Za-z]+/, "")
  const num = +s
  return Number.isFinite(num) ? num : NaN
}

/** Rounded to 2 decimal places for form state; `undefined` when invalid or ≤ 0. */
export const formatPaymentLineAmountForInput = raw => {
  const n = typeof raw === "number" && Number.isFinite(raw) ? raw : parsePaymentLineAmount(raw)
  if (!n || n <= 0) return undefined
  return roundMoneyToTwoDecimals(n)
}

const imageDataModeDetected = imageData =>
  !!(
    imageData &&
    (imageData.paymentModeKey ||
      imageData.payment_mode_key ||
      imageData.PaymentModeKey ||
      (typeof imageData.paymentMode === "string" && imageData.paymentMode.trim() !== ""))
  )

export const rowHasPaymentMode = row => {
  const m = row?.modeOfPayment
  if (m != null && m !== "") {
    if (typeof m === "object") return !!(m?.key ?? m?.value)
    return true
  }
  const img = row?.paymentProof?.imageData
  const ocr = row?.ocrData?.imageData
  return imageDataModeDetected(img) || imageDataModeDetected(ocr)
}

export const isPaymentRowComplete = row => {
  if (!row) return false
  const amt = parsePaymentLineAmount(row.amount)
  if (!(amt > 0)) return false
  if (row.enableRA) {
    return !!row.regional_accountant
  }
  if (row.enableDepositRA === true) {
    const hasProof = hasPaymentProofValue(row.paymentProof)
    const hasMode = rowHasPaymentMode(row)
    return hasProof && hasMode && !!row.regional_accountant
  }
  const hasProof = hasPaymentProofValue(row.paymentProof)
  const hasMode = rowHasPaymentMode(row)
  return hasProof && hasMode
}

export const paymentsListFullyComplete = listValue =>
  Array.isArray(listValue) && listValue.length > 0 && listValue.every(row => isPaymentRowComplete(row))

export const getPaymentHeaderSubtitle = (row, modeOfPayments) => {
  if (row?.enableRA && row?.regional_accountant) {
    return t("Regional Accountant")
  }
  if (row?.enableDepositRA === true && row?.regional_accountant) {
    return t("Regional Accountant")
  }
  const fromVision = row?.paymentProof?.imageData?.paymentMode || row?.ocrData?.imageData?.paymentMode
  if (fromVision && String(fromVision).trim()) return String(fromVision).trim()
  const modeKey =
    typeof row?.modeOfPayment === "object" ? (row?.modeOfPayment?.key ?? row?.modeOfPayment?.value) : row?.modeOfPayment
  return modeOfPayments?.find(m => m.key === modeKey)?.name || modeKey || ""
}

function getFollowUpSuggestionValue(item, previousPaymentsTotal, totalRequiredAmount, milestoneCtx) {
  const prev = previousPaymentsTotal || 0
  const vasRounded = Math.round(milestoneCtx?.vasFees || 0)
  const prevTowardTokenPdpCdp =
    milestoneCtx?.vasEnabled && milestoneCtx?.firstSales ? Math.max(0, prev - vasRounded) : prev

  if (item.key === "token" || item.key === "pdp" || item.key === "cdp") {
    return Math.max(0, Math.round(item.value - prevTowardTokenPdpCdp))
  }
  const poolRemaining = Math.max(totalRequiredAmount - prev, 0)
  return Math.min(item.value, poolRemaining)
}


export const validatePaymentsMatchReferenceTotal = ({ listValue, referenceAmount }) => {
  const refRaw = +referenceAmount || 0
  if (refRaw <= 0) return Promise.resolve()
  const ref = Math.round(refRaw)
  const sum = Math.round((listValue || []).reduce((acc, row) => acc + (+row?.amount || 0), 0))
  if (sum !== ref) {
    return Promise.reject(t("Total payments must equal the sale amount"))
  }
  return Promise.resolve()
}

export const buildPaymentSuggestionItems = ({
  project,
  leadSalesInfo,
  dealPrice,
  cdpCommitment,
  minTokenDetails,
  isEscrowSelfFinance,
  vasEnabled,
  lastPaymentStatus,
  lastPaymentType,
  vasFees,
  rowIndex,
  totalRequiredAmount,
  previousPaymentsTotal,
  listFieldName,
}) => {
  const calculateValue = (value, unit, fallback) =>
    value !== undefined ? (unit === "percentage" ? (value / 100) * dealPrice : value) : fallback

  const token = calculateValue(
    leadSalesInfo?.token,
    leadSalesInfo?.tokenUnit,
    calculateValue(minTokenDetails.minToken, minTokenDetails.minTokenUnit, 0)
  )
  const pdp = leadSalesInfo?.remainParDowPayment ?? project?.pdpPercentage * dealPrice
  const cdp =
    leadSalesInfo?.remainComDowPayment ??
    ((cdpCommitment ?? project?.ProjectFinancial?.completeDownPayment) / 100) * dealPrice

  const items = []
  const addItem = (value, suffix, key) => {
    if (value == null) return
    const v = +value
    if (v !== v) return
    items.push({ key, value: Math.round(v), suffix })
  }

  const firstSales = leadSalesInfo?.isFirstSalesTask
  const vasRemaining = leadSalesInfo?.isVasRemaining
  const rejectedToken = lastPaymentStatus === "rejected" && lastPaymentType === "token"
  const prev = previousPaymentsTotal || 0
  const vasRounded = Math.round(vasFees || 0)
  const bsfCoveredByPriorRows = vasEnabled && firstSales && vasRounded > 0 && prev >= vasRounded

  if (vasEnabled && firstSales) {
    if (!bsfCoveredByPriorRows) {
      addItem(vasFees, "(BSF Fee)", "vas")
    } else if (!isEscrowSelfFinance) {
      addItem(pdp, "(PDP)", "pdp")
      addItem(cdp, "(CDP)", "cdp")
    }
  } else if (vasEnabled && vasRemaining) {
    addItem(leadSalesInfo?.vasRemianingAmount, "(BSF Remaining Fee)", "vasRemain")
  } else if (!leadSalesInfo || firstSales || rejectedToken) {
    addItem(token, "(Token)", "token")
    if (!isEscrowSelfFinance) {
      addItem(pdp, "(PDP)", "pdp")
      addItem(cdp, "(CDP)", "cdp")
    }
  } else {
    if (pdp > 0) addItem(pdp, "(PDP)", "pdp")
    if (cdp > 0) addItem(cdp, "(CDP)", "cdp")
  }

  const suffix = listFieldName != null ? `-p${listFieldName}` : ""
  const milestoneCtx = { vasEnabled, firstSales, vasFees }

  if (rowIndex <= 0) {
    return items.map(item => ({ ...item, key: `${item.key}${suffix}` }))
  }

  return items
    .map(item => {
      const suggested = getFollowUpSuggestionValue(item, prev, totalRequiredAmount, milestoneCtx)
      return suggested > 0 ? { ...item, value: suggested, key: `${item.key}${suffix}` } : null
    })
    .filter(Boolean)
}
